// src/pages/CourseView.js
//
// REESCRITO 03-oct-26 — Aula virtual del estudiante conectada al backend.
//
// Antes esta pantalla importaba de ../data/courses y ../data/quizzes
// (archivos estáticos legacy del demo original Prosumo) y nunca llamaba
// a la API. Resultado: en producción daba "Curso no encontrado" para
// cualquier curso real de la BD.
//
// Ahora:
//   - cursoService.getBySlug(slug)      → curso + unidades + contenidos
//   - meService.getMisCursos()          → matrícula del usuario (si tiene)
//   - progressStore.fetchProgress(mId)  → progreso de la matrícula
//   - progressStore.markMaterialRead()  → completar un material
//   - progressStore.updateVideoProgress() → avance de video
//
// Si el usuario abre /learn/<slug> sin estar matriculado, se le
// redirige a /course/<slug> para que se inscriba primero.
//
// Los tipos de contenido se alinean con el enum TipoContenido del
// backend (PascalCase): Material, Video, Quiz, Actividad, Examen,
// SesionOnline, SesionGrabada, Foro.
//
// Contenidos de tipo Quiz / Actividad / Examen / SesionOnline /
// SesionGrabada / Foro todavía no tienen UI completa en esta vista
// (sus endpoints específicos existen en progressStore pero el flujo
// UI necesita trabajo propio). Por ahora muestran un placeholder
// claro — se migran en PRs separados por tipo.

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
  Chip,
  LinearProgress,
  Collapse,
  Alert,
  Divider,
  Paper,
  Skeleton,
  TextField,
  Stack,
  Container,
} from '@mui/material';
import {
  ArrowBack,
  PlayCircle,
  Quiz as QuizIcon,
  Assignment,
  Description,
  School,
  Forum,
  VideoCall,
  OndemandVideo,
  CheckCircle,
  ExpandMore,
  ExpandLess,
  Lock,
  CloudUpload,
  EmojiEvents,
  Refresh,
} from '@mui/icons-material';
import useProgressStore from '../context/progressStore';
import { useAuth } from '../context/AppContext';
import { cursoService, meService, aprendizajeService } from '../services/api';
import { toHtml } from '../utils/markdown';

const DRAWER_WIDTH = 340;

// ─── Mapeo de tipos del backend ───────────────────────────────────

const TYPE_ICONS = {
  Material:       Description,
  Video:          PlayCircle,
  Quiz:           QuizIcon,
  Actividad:      Assignment,
  Examen:         School,
  SesionOnline:   VideoCall,
  SesionGrabada:  OndemandVideo,
  Foro:           Forum,
};

const TYPE_LABELS = {
  Material:       'Material',
  Video:          'Video',
  Quiz:           'Cuestionario',
  Actividad:      'Actividad',
  Examen:         'Examen',
  SesionOnline:   'Sesión online',
  SesionGrabada:  'Sesión grabada',
  Foro:           'Foro',
};

const TYPE_COLORS = {
  Material:       '#3b82f6',
  Video:          '#8b5cf6',
  Quiz:           '#f59e0b',
  Actividad:      '#10b981',
  Examen:         '#ef4444',
  SesionOnline:   '#06b6d4',
  SesionGrabada:  '#06b6d4',
  Foro:           '#a855f7',
};

// toHtml(value) está centralizado en src/utils/markdown.js.
// Si el valor en BD ya es HTML (viene del editor WYSIWYG del admin),
// se devuelve tal cual. Si es Markdown (del seed sql/015), se convierte.
// Permite mezclar legacy y nuevo sin breaking changes.

// ─── Renderers por tipo de contenido ──────────────────────────────

function MaterialView({ content, matriculaId, completed, onCompleted }) {
  const markMaterialRead = useProgressStore((s) => s.markMaterialRead);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleMarkRead = async () => {
    setSaving(true);
    setError('');
    try {
      await markMaterialRead(matriculaId, content.id);
      onCompleted?.(content.id);
    } catch (e) {
      setError(e.message || 'No se pudo marcar como completado.');
    } finally {
      setSaving(false);
    }
  };

  const html = useMemo(() => toHtml(content.body), [content.body]);

  return (
    <Box>
      <Paper sx={{ p: { xs: 2, md: 4 }, mb: 3 }}>
        {html ? (
          <Box
            sx={{
              '& h1': { fontSize: '1.8rem', fontWeight: 700, mb: 2, color: 'primary.main' },
              '& h2': { fontSize: '1.4rem', fontWeight: 600, mt: 3, mb: 1.5 },
              '& h3': { fontSize: '1.1rem', fontWeight: 600, mt: 2, mb: 1 },
              '& p': { mb: 1.5, lineHeight: 1.75 },
              '& ul, & ol': { pl: 3, mb: 2 },
              '& li': { mb: 0.5 },
              '& code': { bgcolor: 'rgba(0,0,0,0.06)', px: 0.8, py: 0.3, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.9em' },
              '& pre': { bgcolor: '#1a1a2e', color: '#f5f5f5', p: 2, borderRadius: 2, overflow: 'auto', mb: 2 },
              '& table': { width: '100%', borderCollapse: 'collapse', mb: 2 },
              '& th, & td': { border: '1px solid rgba(0,0,0,0.12)', p: 1, textAlign: 'left' },
              '& th': { bgcolor: 'rgba(0,0,0,0.04)', fontWeight: 600 },
              '& strong': { fontWeight: 700 },
            }}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        ) : (
          <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Este material aún no tiene contenido. El instructor lo cargará próximamente.
          </Typography>
        )}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!completed ? (
        <Button
          variant="contained"
          startIcon={<CheckCircle />}
          onClick={handleMarkRead}
          disabled={saving || !matriculaId}
          size="large"
        >
          {saving ? 'Guardando...' : 'Marcar como leído'}
        </Button>
      ) : (
        <Alert severity="success" icon={<CheckCircle />}>
          Has completado esta lectura
        </Alert>
      )}
    </Box>
  );
}

function VideoView({ content, matriculaId, completed, onCompleted }) {
  const markMaterialRead = useProgressStore((s) => s.markMaterialRead);
  const [saving, setSaving] = useState(false);

  if (!content.resourceUrl) {
    return (
      <Alert severity="info">
        Este video aún no tiene URL. El instructor lo cargará próximamente.
      </Alert>
    );
  }

  const handleMarkWatched = async () => {
    setSaving(true);
    try { await markMaterialRead(matriculaId, content.id); onCompleted?.(content.id); }
    finally { setSaving(false); }
  };

  return (
    <Box>
      <Paper sx={{ overflow: 'hidden', mb: 3 }}>
        <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
          <iframe
            src={content.resourceUrl}
            title={content.title}
            style={{
              position: 'absolute', top: 0, left: 0,
              width: '100%', height: '100%', border: 0,
            }}
            allowFullScreen
          />
        </Box>
      </Paper>
      {!completed ? (
        <Button variant="contained" startIcon={<CheckCircle />} onClick={handleMarkWatched} disabled={saving || !matriculaId}>
          {saving ? 'Guardando...' : 'Marcar como visto'}
        </Button>
      ) : (
        <Alert severity="success" icon={<CheckCircle />}>Has visto este video</Alert>
      )}
    </Box>
  );
}

// ─── Marca de agua translúcida con el email del estudiante ────────
// Patrón repetido en diagonal. Visible al ojo pero no estorba la
// lectura. Si el estudiante hace screenshot, su email queda en la
// imagen y lo identifica como fuente de la filtración.

function WatermarkOverlay({ email }) {
  if (!email) return null;
  const texto = email;
  // Repetimos la etiqueta varias veces en una banda diagonal
  const banda = Array.from({ length: 12 }, (_, i) => (
    <span key={i} style={{ marginRight: 48 }}>{texto}</span>
  ));
  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute', inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
        zIndex: 1,
        opacity: 0.08,
        userSelect: 'none',
      }}
    >
      {Array.from({ length: 10 }, (_, row) => (
        <Box key={row} sx={{
          position: 'absolute',
          top: `${row * 180 - 100}px`,
          left: '-20%',
          width: '140%',
          transform: 'rotate(-28deg)',
          whiteSpace: 'nowrap',
          fontFamily: 'monospace',
          fontSize: '0.95rem',
          color: 'currentColor',
          fontWeight: 600,
        }}>
          {banda}
        </Box>
      ))}
    </Box>
  );
}

// ─── Quiz / Examen ────────────────────────────────────────────────

function QuizView({ content, matriculaId, completed, onCompleted }) {
  const iniciarIntentoQuiz = useProgressStore((s) => s.iniciarIntentoQuiz);
  const submitQuizAttempt  = useProgressStore((s) => s.submitQuizAttempt);
  const getQuizPreguntas   = useProgressStore((s) => s.getQuizPreguntas);
  const getAttemptsMade    = useProgressStore((s) => s.getAttemptsMade);
  const getScore           = useProgressStore((s) => s.getScore);

  const [stage, setStage] = useState('idle');   // idle | running | result
  const [preguntas, setPreguntas] = useState([]);
  const [respuestas, setRespuestas] = useState({});   // { preguntaId: opcionId }
  const [attemptId, setAttemptId] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const intentos = getAttemptsMade(content.id);
  const puntajePrevio = getScore(content.id);
  const maxIntentos = content.maxAttempts || null;   // null = ilimitado
  const puedeIntentar = maxIntentos ? intentos < maxIntentos : true;

  const iniciar = async () => {
    setError(''); setLoading(true);
    const [preg, intento] = await Promise.all([
      getQuizPreguntas(content.id),
      iniciarIntentoQuiz(matriculaId, content.id),
    ]);
    setLoading(false);
    if (!intento?.success) { setError(intento?.error || 'No se pudo iniciar el intento.'); return; }
    if (!preg?.length)     { setError('Este cuestionario aún no tiene preguntas.'); return; }
    setPreguntas(preg);
    setAttemptId(intento.attemptId);
    setRespuestas({});
    setStage('running');
  };

  const enviar = async () => {
    if (preguntas.some((p) => !respuestas[p.id])) {
      setError('Responde todas las preguntas antes de enviar.');
      return;
    }
    setError(''); setLoading(true);
    const payload = preguntas.map((p) => ({ questionId: p.id, optionId: respuestas[p.id] }));
    const r = await submitQuizAttempt(matriculaId, attemptId, payload);
    setLoading(false);
    if (!r?.success) { setError(r?.message || 'Error enviando el intento.'); return; }
    setResultado(r);
    setStage('result');
    if (r.passed) onCompleted?.(content.id);
  };

  // ── Estado inicial / historial ────────────────────────────────
  if (stage === 'idle') {
    return (
      <Box>
        {completed && (
          <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 2 }}>
            Ya aprobaste este cuestionario{puntajePrevio != null ? ` con ${Number(puntajePrevio).toFixed(1)} puntos` : ''}.
          </Alert>
        )}
        {!completed && intentos > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Tienes {intentos} intento{intentos > 1 ? 's' : ''} previos sin aprobar
            {puntajePrevio != null ? ` (mejor puntaje: ${Number(puntajePrevio).toFixed(1)})` : ''}.
          </Alert>
        )}

        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {content.description || 'Cuestionario de evaluación del módulo.'}
          </Typography>
          <Stack spacing={1} sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
            <div>• Puntaje total: {content.maxPoints || '—'} pts</div>
            <div>• Puntaje mínimo para aprobar: {content.minScore ?? '70'}%</div>
            <div>• Intentos: {maxIntentos ? `${intentos} / ${maxIntentos}` : `${intentos} (ilimitado)`}</div>
            {content.durationMinutes > 0 && <div>• Tiempo sugerido: {content.durationMinutes} min</div>}
          </Stack>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Button
          variant="contained"
          size="large"
          onClick={iniciar}
          disabled={loading || !puedeIntentar}
          startIcon={<QuizIcon />}
        >
          {loading ? 'Preparando...' : completed ? 'Volver a intentar' : intentos > 0 ? 'Reintentar cuestionario' : 'Iniciar cuestionario'}
        </Button>
        {!puedeIntentar && (
          <Typography variant="caption" sx={{ ml: 2, color: 'error.main' }}>
            Agotaste los intentos permitidos.
          </Typography>
        )}
      </Box>
    );
  }

  // ── Preguntas ─────────────────────────────────────────────────
  if (stage === 'running') {
    return (
      <Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          Responde todas las preguntas y presiona enviar. Se califica automáticamente.
        </Alert>
        {preguntas.map((p, idx) => (
          <Paper key={p.id} sx={{ p: 3, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              {idx + 1}. {p.statement}
            </Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {p.options.map((o) => (
                <Paper
                  key={o.id}
                  onClick={() => setRespuestas((prev) => ({ ...prev, [p.id]: o.id }))}
                  sx={{
                    p: 1.5, cursor: 'pointer',
                    border: 2,
                    borderColor: respuestas[p.id] === o.id ? 'primary.main' : 'divider',
                    bgcolor: respuestas[p.id] === o.id ? 'action.selected' : 'transparent',
                    '&:hover': { borderColor: 'primary.light' },
                  }}
                  variant="outlined"
                >
                  <Typography variant="body2">{o.text}</Typography>
                </Paper>
              ))}
            </Stack>
          </Paper>
        ))}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Button variant="contained" size="large" onClick={enviar} disabled={loading}>
          {loading ? 'Enviando...' : 'Enviar respuestas'}
        </Button>
      </Box>
    );
  }

  // ── Resultado ─────────────────────────────────────────────────
  const pct = Number(resultado.percent || 0).toFixed(1);
  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3, textAlign: 'center' }}>
        {resultado.passed ? (
          <>
            <EmojiEvents sx={{ fontSize: 64, color: 'success.main', mb: 1 }} />
            <Typography variant="h5" fontWeight={700} color="success.main">
              ¡Aprobado!
            </Typography>
          </>
        ) : (
          <>
            <Refresh sx={{ fontSize: 64, color: 'warning.main', mb: 1 }} />
            <Typography variant="h5" fontWeight={700} color="warning.main">
              No alcanzaste el mínimo
            </Typography>
          </>
        )}
        <Typography variant="h3" sx={{ mt: 1 }}>
          {resultado.scoreObtained} / {resultado.maxScore}
        </Typography>
        <Typography color="text.secondary">{pct}%</Typography>
      </Paper>

      <Typography variant="h6" sx={{ mb: 2 }}>Detalle por pregunta</Typography>
      {resultado.detail.map((d, i) => (
        <Paper key={d.questionId} sx={{ p: 2, mb: 1.5, borderLeft: 4, borderColor: d.isCorrect ? 'success.main' : 'error.main' }}>
          <Typography variant="body2" fontWeight={600}>
            {i + 1}. {d.statement}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
            <Chip
              size="small"
              label={d.isCorrect ? 'Correcta' : 'Incorrecta'}
              color={d.isCorrect ? 'success' : 'error'}
            />
          </Stack>
          {d.explanation && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {d.explanation}
            </Typography>
          )}
        </Paper>
      ))}

      {!resultado.passed && puedeIntentar && (
        <Button
          variant="contained"
          sx={{ mt: 2 }}
          onClick={() => { setStage('idle'); setResultado(null); setRespuestas({}); }}
        >
          Reintentar
        </Button>
      )}
    </Box>
  );
}

// ─── Actividad (entrega de archivos) ──────────────────────────────

function ActivityView({ content, matriculaId, completed, onCompleted }) {
  const submitActivity      = useProgressStore((s) => s.submitActivity);
  const subirArchivoEntrega = useProgressStore((s) => s.subirArchivoEntrega);
  const getProgressId       = useProgressStore((s) => s.getProgressId);
  const getScore            = useProgressStore((s) => s.getScore);

  const progressId = getProgressId(content.id);
  const score      = getScore(content.id);

  const [comentario, setComentario] = useState('');
  const [archivos, setArchivos]     = useState([]);
  const [sending, setSending]       = useState(false);
  const [done, setDone]             = useState(false);
  const [error, setError]           = useState('');

  const enviar = async () => {
    if (!progressId) { setError('No se pudo resolver el progreso del contenido.'); return; }
    if (!comentario.trim() && archivos.length === 0) {
      setError('Agrega un comentario o al menos un archivo.');
      return;
    }
    setError(''); setSending(true);
    try {
      const r = await submitActivity(progressId, comentario, [], matriculaId);
      if (!r?.success) { setError(r?.error || 'Error creando la entrega.'); setSending(false); return; }
      // Subir archivos uno por uno a la entrega recién creada
      for (const file of archivos) {
        await subirArchivoEntrega(r.entregaId, file);
      }
      setDone(true);
      onCompleted?.(content.id);
    } catch (e) {
      setError(e.message || 'Error inesperado.');
    } finally {
      setSending(false);
    }
  };

  if (completed || done) {
    return (
      <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>Entrega registrada</Typography>
        <Typography variant="body2">
          Tu entrega fue recibida. El instructor la calificará y verás la nota aquí{' '}
          {score != null ? `(nota actual: ${Number(score).toFixed(1)})` : 'cuando esté lista.'}
        </Typography>
      </Alert>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {content.description || 'Entrega la actividad para que el instructor la revise.'}
        </Typography>
        <Stack spacing={1} sx={{ color: 'text.secondary', fontSize: '0.9rem', mb: 2 }}>
          <div>• Puntaje total: {content.maxPoints || '—'} pts</div>
          {content.durationMinutes > 0 && <div>• Tiempo estimado: {content.durationMinutes} min</div>}
        </Stack>
      </Paper>

      <TextField
        label="Comentario del estudiante"
        value={comentario}
        onChange={(e) => setComentario(e.target.value)}
        fullWidth multiline minRows={4}
        placeholder="Describe tu entrega, resumen de tu trabajo, o notas al instructor..."
        sx={{ mb: 2 }}
      />

      <Box sx={{ mb: 2 }}>
        <Button variant="outlined" component="label" startIcon={<CloudUpload />}>
          Adjuntar archivos
          <input
            hidden multiple type="file"
            onChange={(e) => setArchivos(Array.from(e.target.files || []))}
          />
        </Button>
        {archivos.length > 0 && (
          <Stack sx={{ mt: 1.5 }} spacing={0.5}>
            {archivos.map((f, i) => (
              <Chip
                key={i} label={`${f.name} (${(f.size / 1024).toFixed(1)} KB)`}
                onDelete={() => setArchivos(archivos.filter((_, j) => j !== i))}
                size="small"
              />
            ))}
          </Stack>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Button
        variant="contained" size="large"
        onClick={enviar} disabled={sending}
        startIcon={<Assignment />}
      >
        {sending ? 'Enviando...' : 'Entregar actividad'}
      </Button>
    </Box>
  );
}

// ─── Página principal ─────────────────────────────────────────────

function CourseViewPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const userEmail = user?.email || '';

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // 401/403 -> sin sesion o no autorizado; distinto de "no existe".
  const [sinAcceso, setSinAcceso] = useState(false);
  const [errorCarga, setErrorCarga] = useState(null);
  const [matriculaId, setMatriculaId] = useState(null);
  const [notEnrolled, setNotEnrolled] = useState(false);

  const fetchProgress      = useProgressStore((s) => s.fetchProgress);
  const isContentCompleted = useProgressStore((s) => s.isContentCompleted);
  const isContentUnlocked  = useProgressStore((s) => s.isContentUnlocked);
  const courseProgress     = useProgressStore((s) => s.courseProgress);

  const [expandedUnits, setExpandedUnits] = useState([]);
  const [selectedContentId, setSelectedContentId] = useState(null);

  // 1. Cargar curso + resolver matrícula del usuario
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isAuthenticated) {
        navigate(`/login`, { state: { from: `/learn/${slug}` } });
        return;
      }
      setLoading(true);
      setNotFound(false);
      setSinAcceso(false);
      setErrorCarga(null);
      setNotEnrolled(false);
      try {
        const data = await cursoService.getBySlug(slug);
        if (cancelled) return;
        if (!data) { setNotFound(true); return; }
        setCourse(data);

        // Buscar la matrícula del usuario en ESTE curso
        const mis = await meService.getMisCursos().catch(() => []);
        const match = (mis || []).find((c) => c.id === data.id || c.courseId === data.id);
        const mId = match?.enrollmentId || match?.matriculaId || null;

        if (!mId) {
          setNotEnrolled(true);
          setExpandedUnits(data.units?.[0]?.id ? [data.units[0].id] : []);
          return;
        }
        if (cancelled) return;
        setMatriculaId(mId);
        await fetchProgress(mId);

        // Resume: posicionar al estudiante donde iba la ultima vez.
        // match.lastViewedContentId viene de GET /me/cursos y se
        // actualiza en cada POST /aprendizaje/acceso del backend.
        // Si no hay (NULL o el contenido ya no existe en el curso),
        // caemos al fallback del useMemo selectedContent, que elige
        // el primer no completado y desbloqueado.
        const lastId = match?.lastViewedContentId;
        let resumedUnitId = null;
        if (lastId) {
          for (const u of (data.units || [])) {
            if ((u.contents || []).some((c) => c.id === lastId)) {
              resumedUnitId = u.id;
              if (!cancelled) setSelectedContentId(lastId);
              break;
            }
          }
        }
        if (!cancelled) {
          setExpandedUnits(resumedUnitId
            ? [resumedUnitId]
            : (data.units?.[0]?.id ? [data.units[0].id] : []));
        }
      } catch (e) {
        if (!cancelled) {
          console.warn('CourseView: fallo cargando curso', e);
          if (e?.status === 401 || e?.status === 403) setSinAcceso(true);
          else if (e?.status === 404) setNotFound(true);
          else setErrorCarga(e?.message || 'Intenta de nuevo en unos minutos.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, isAuthenticated, navigate, fetchProgress]);

  // Lista plana de contenidos (para seleccionar el "siguiente pendiente")
  const allContents = useMemo(() => {
    if (!course?.units) return [];
    return course.units.flatMap((u) => (u.contents || []).map((c) => ({
      ...c, unitId: u.id, unitTitle: u.title,
    })));
  }, [course]);

  const selectedContent = useMemo(() => {
    if (!allContents.length) return null;
    // Si el usuario eligió uno explícitamente y está desbloqueado, respetarlo.
    if (selectedContentId) {
      const chosen = allContents.find((c) => c.id === selectedContentId);
      if (chosen && isContentUnlocked(chosen.id)) return chosen;
    }
    // Si no, el primero no completado y desbloqueado — el "siguiente pendiente".
    const next = allContents.find((c) => isContentUnlocked(c.id) && !isContentCompleted(c.id));
    if (next) return next;
    // Si todo está completo, el último contenido desbloqueado.
    const lastUnlocked = [...allContents].reverse().find((c) => isContentUnlocked(c.id));
    return lastUnlocked || allContents[0];
  }, [selectedContentId, allContents, isContentUnlocked, isContentCompleted]);

  const toggleUnit = useCallback((unitId) => {
    setExpandedUnits((prev) => prev.includes(unitId) ? prev.filter((id) => id !== unitId) : [...prev, unitId]);
  }, []);

  const onContentCompleted = useCallback(async (completedId) => {
    if (!matriculaId) return;
    // 1. Refrescar progreso para que isContentUnlocked / isContentCompleted
    //    del store reflejen el nuevo estado desde el backend.
    await fetchProgress(matriculaId);

    // 2. Encontrar el siguiente contenido desbloqueado y no completado
    //    DESPUES del que acabamos de completar en el orden plano del curso.
    //    Si no encuentra, no hacemos nada — el estudiante se queda en el
    //    actual (último del curso o bloqueo pendiente de otro flujo).
    if (!completedId || !allContents.length) return;
    const idx = allContents.findIndex((c) => c.id === completedId);
    if (idx < 0) return;

    // Leer el store fresco (no el closure) para los estados post-fetch.
    const store = useProgressStore.getState();
    const siguiente = allContents.slice(idx + 1).find(
      (c) => store.isContentUnlocked(c.id) && !store.isContentCompleted(c.id)
    );
    if (!siguiente) return;

    // 3. Navegar: cambiar seleccion + expandir la unidad del siguiente si
    //    estaba colapsada (p.ej. al pasar del ultimo material del Modulo 1
    //    al primero del Modulo 2).
    setSelectedContentId(siguiente.id);
    setExpandedUnits((prev) =>
      prev.includes(siguiente.unitId) ? prev : [...prev, siguiente.unitId]
    );
  }, [matriculaId, fetchProgress, allContents]);

  // Registrar acceso cada vez que el estudiante abre un contenido.
  // Silencioso si falla — no debe interrumpir el flujo del estudiante.
  useEffect(() => {
    if (matriculaId && selectedContent?.id) {
      aprendizajeService.registrarAcceso(matriculaId, selectedContent.id);
    }
  }, [matriculaId, selectedContent?.id]);

  // ─── Estados UI ─────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ p: 4 }}>
        <Skeleton variant="rounded" height={60} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={300} />
      </Box>
    );
  }

  if (sinAcceso) {
    return (
      <Container sx={{ py: 10, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>Inicia sesión para ver este curso</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          El contenido de los cursos está disponible solo para usuarios registrados.
        </Typography>
        <Button variant="contained" sx={{ mr: 2 }}
          onClick={() => navigate('/login', { state: { from: `/learn/${slug}` } })}>
          Iniciar sesión
        </Button>
        <Button component={Link} to="/courses" variant="outlined">Ver cursos</Button>
      </Container>
    );
  }

  if (errorCarga) {
    return (
      <Container sx={{ py: 10, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>No se pudo cargar el curso</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>{errorCarga}</Typography>
        <Button variant="contained" sx={{ mr: 2 }} onClick={() => window.location.reload()}>Reintentar</Button>
        <Button component={Link} to="/courses" variant="outlined">Ver cursos</Button>
      </Container>
    );
  }

  if (notFound || !course) {
    return (
      <Box sx={{ p: 10, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>Curso no encontrado</Typography>
        <Button component={Link} to="/courses" variant="contained" sx={{ mt: 2 }}>
          Ver cursos
        </Button>
      </Box>
    );
  }

  if (notEnrolled) {
    return (
      <Box sx={{ p: 10, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>Debes inscribirte primero</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Para acceder al aula virtual del curso <strong>{course.title}</strong> necesitas estar matriculado.
        </Typography>
        <Button component={Link} to={`/course/${slug}`} variant="contained" size="large">
          Ir al detalle del curso para inscribirme
        </Button>
      </Box>
    );
  }

  // ─── Render contenido seleccionado ──────────────────────────────
  const renderContent = () => {
    if (!selectedContent) return null;
    const common = {
      content: selectedContent,
      matriculaId,
      completed: isContentCompleted(selectedContent.id),
      onCompleted: onContentCompleted,
    };
    switch (selectedContent.type) {
      case 'Material':       return <MaterialView {...common} />;
      case 'Video':          return <VideoView {...common} />;
      case 'Quiz':           return <QuizView {...common} />;
      case 'Examen':         return <QuizView {...common} />;
      case 'Actividad':      return <ActivityView {...common} />;
      case 'SesionOnline':
      case 'SesionGrabada':
      case 'Foro':
        return (
          <Alert severity="info">
            El tipo "{TYPE_LABELS[selectedContent.type]}" todavía no tiene vista interactiva.
            Está en el roadmap.
          </Alert>
        );
      default:               return <Alert severity="warning">Tipo de contenido no soportado: {selectedContent.type}</Alert>;
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, top: 64, height: 'calc(100% - 64px)' },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Button component={Link} to={`/course/${slug}`} startIcon={<ArrowBack />} size="small" sx={{ mb: 2 }}>
            Volver al detalle
          </Button>
          <Typography variant="subtitle1" fontWeight={700} noWrap>{course.title}</Typography>
          <Box sx={{ mt: 2, mb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">Progreso</Typography>
              <Typography variant="caption" color="success.main" fontWeight={600}>
                {courseProgress}%
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={courseProgress} color="success" sx={{ height: 6, borderRadius: 3 }} />
          </Box>
        </Box>

        <Divider />

        <Box sx={{ overflow: 'auto', flex: 1 }}>
          {course.units?.map((unit, ui) => (
            <Box key={unit.id}>
              <ListItemButton onClick={() => toggleUnit(unit.id)} sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                <ListItemText
                  primary={`${ui + 1}. ${unit.title}`}
                  primaryTypographyProps={{ fontWeight: 600, fontSize: '0.85rem' }}
                />
                {expandedUnits.includes(unit.id) ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
              <Collapse in={expandedUnits.includes(unit.id)}>
                <List disablePadding>
                  {(unit.contents || []).map((content) => {
                    const completed = isContentCompleted(content.id);
                    const unlocked  = isContentUnlocked(content.id);
                    const isCurrent = selectedContent?.id === content.id;
                    const Icon = TYPE_ICONS[content.type] || Description;
                    return (
                      <ListItemButton
                        key={content.id}
                        onClick={() => unlocked && setSelectedContentId(content.id)}
                        disabled={!unlocked}
                        selected={isCurrent}
                        sx={{
                          pl: 3,
                          borderLeft: isCurrent ? 3 : 0,
                          borderColor: 'primary.main',
                          opacity: unlocked ? 1 : 0.55,
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {completed
                            ? <CheckCircle color="success" fontSize="small" />
                            : !unlocked
                              ? <Lock fontSize="small" sx={{ color: 'text.disabled' }} />
                              : <Icon fontSize="small" sx={{ color: TYPE_COLORS[content.type] }} />}
                        </ListItemIcon>
                        <ListItemText
                          primary={content.title}
                          primaryTypographyProps={{ fontSize: '0.82rem', noWrap: true }}
                          secondary={
                            !unlocked
                              ? 'Bloqueado — completa el anterior'
                              : content.maxPoints > 0
                                ? `${content.maxPoints} pts`
                                : null
                          }
                          secondaryTypographyProps={{ fontSize: '0.7rem' }}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Collapse>
            </Box>
          ))}
        </Box>
      </Drawer>

      {/* Main con protecciones:
           - position relative para montar el watermark absoluto
           - user-select none bloquea seleccionar texto
           - onCopy / onCut / onContextMenu / onDragStart bloquean copiar
             y arrastrar. No es seguridad real pero sube el costo del
             robo y marca la intencion.
           - WatermarkOverlay con el email del estudiante en diagonal. */}
      <Box
        component="main"
        onCopy={(e) => e.preventDefault()}
        onCut={(e) => e.preventDefault()}
        onContextMenu={(e) => e.preventDefault()}
        onDragStart={(e) => e.preventDefault()}
        sx={{
          flexGrow: 1,
          minHeight: '100vh',
          position: 'relative',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none',
        }}
      >
        <WatermarkOverlay email={userEmail} />
        {selectedContent && (
          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={TYPE_LABELS[selectedContent.type] || selectedContent.type}
                  size="small"
                  sx={{ bgcolor: TYPE_COLORS[selectedContent.type], color: 'white' }}
                />
                {selectedContent.maxPoints > 0 && (
                  <Chip label={`${selectedContent.maxPoints} puntos`} size="small" variant="outlined" />
                )}
              </Box>
              <Typography variant="h5" fontWeight={700}>{selectedContent.title}</Typography>
              {selectedContent.description && (
                <Typography color="text.secondary" sx={{ mt: 0.5 }}>{selectedContent.description}</Typography>
              )}
            </Box>
            <Box sx={{ p: { xs: 2, md: 4 } }}>{renderContent()}</Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default CourseViewPage;
